import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Search, Download, Copy, ArrowUpRight, TrendingUp, Flame, Loader2, History,
  X, Send, ChevronUp, ChevronDown, Star, Bookmark, FolderPlus, Plus,
  Sparkles, ArrowRight, Trash2, BookOpen, Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useCredits } from "@/hooks/useCredits";
import keywordLogo from "@/assets/keyword-research-logo-new.png";

// ── Interfaces ────────────────────────────────────────────────
interface MainKeyword {
  keyword: string;
  opportunityScore: number;
  trendStatus: string;
  trendEmoji: string;
  searchVolume: string;
  competition: string;
  explanation: string;
}

interface RelatedKeyword {
  keyword: string;
  opportunityScore: number;
  trendStrength: string;
  competition: string;
  relevance?: number;
}

interface TitleAnalysis {
  relevanceScore: number;
  advice: string;
}

interface TrendingKeyword {
  keyword: string;
  niche: string;
  opportunity_score: number;
  trend_interest: number;
  competition_level: string;
  long_tails: string[];
  last_refreshed: string | null;
}

interface SearchResult {
  mainKeyword: MainKeyword;
  relatedKeywords: RelatedKeyword[];
  titleAnalysis: TitleAnalysis | null;
  trendingNow: TrendingKeyword[];
}

interface SearchHistoryItem {
  id: string;
  keyword: string;
  video_title: string | null;
  created_at: string;
  results: any;
}

interface SavedKeyword {
  id: string;
  keyword: string;
  niche: string | null;
  opportunity_score: number;
  competition_level: string;
  trend_interest: number;
  source: string;
  created_at: string;
}

interface KeywordGroup {
  id: string;
  name: string;
  keywords: string[];
  created_at: string;
  updated_at: string;
}

// ── Constants ─────────────────────────────────────────────────
const NICHES = [
  "All", "Fitness", "Business", "Finance", "Food", "Lifestyle",
  "Tech", "Gaming", "Education", "Beauty", "Travel",
];

const NICHE_STORAGE_KEY = "keyword-research-niche";

// ── Helpers ───────────────────────────────────────────────────
const scoreColor = (score: number) => {
  if (score >= 80) return "text-green-400";
  if (score >= 60) return "text-emerald-400";
  if (score >= 40) return "text-yellow-400";
  if (score >= 20) return "text-orange-400";
  return "text-red-400";
};

const scoreBg = (score: number) => {
  if (score >= 80) return "bg-green-500/20 border-green-500/30";
  if (score >= 60) return "bg-emerald-500/20 border-emerald-500/30";
  if (score >= 40) return "bg-yellow-500/20 border-yellow-500/30";
  if (score >= 20) return "bg-orange-500/20 border-orange-500/30";
  return "bg-red-500/20 border-red-500/30";
};

const competitionBadge = (level: string) => {
  const colors: Record<string, string> = {
    very_low: "bg-green-500/20 text-green-400 border-green-500/30",
    low: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    very_high: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  return colors[level] || colors.medium;
};

const trendBadge = (strength: string) => {
  const colors: Record<string, string> = {
    hot: "bg-red-500/20 text-red-400 border-red-500/30",
    warm: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    stable: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    cold: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  };
  return colors[strength] || colors.stable;
};

const TrendArrow = ({ interest }: { interest: number }) => {
  if (interest >= 50) return <span className="text-green-400 font-medium">↑</span>;
  if (interest >= 20) return <span className="text-emerald-400">↗</span>;
  if (interest > 0) return <span className="text-muted-foreground">→</span>;
  return <span className="text-red-400">↓</span>;
};

// Mini sparkline component — renders a tiny trend line from 5 random points
const MiniSparkline = ({ interest }: { interest: number }) => {
  const height = 16;
  const width = 40;
  // Generate a simple trend curve based on interest
  const base = Math.max(2, (interest / 100) * height);
  const points = [
    base * 0.5,
    base * 0.7,
    base * 0.6,
    base * 0.85,
    base,
  ];
  const path = points.map((y, i) => `${(i / (points.length - 1)) * width},${height - y}`).join(" ");
  const color = interest >= 50 ? "hsl(142, 71%, 45%)" : interest >= 20 ? "hsl(160, 60%, 45%)" : "hsl(215, 20%, 55%)";

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-10 h-4 inline-block ml-1 align-middle">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={path} />
    </svg>
  );
};

const SortIcon = ({ active, dir }: { active: boolean; dir: "asc" | "desc" }) => {
  if (!active) return <span className="opacity-40 ml-1">↕</span>;
  return dir === "desc" ? <ChevronDown className="w-3 h-3 inline ml-1" /> : <ChevronUp className="w-3 h-3 inline ml-1" />;
};

type PageTab = "overview" | "top-opportunities" | "rising-keywords" | "history";

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function KeywordResearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { consume } = useCredits();

  // ── State ─────────────────────────────────────────────────
  const [keyword, setKeyword] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SearchResult | null>(null);
  const [sortBy, setSortBy] = useState<"opportunityScore" | "competition" | "relevance">("opportunityScore");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<PageTab>("overview");
  const [resultsSubTab, setResultsSubTab] = useState<"overview" | "related">("overview");
  const [loadingText, setLoadingText] = useState("");

  // Trending
  const [allTrending, setAllTrending] = useState<TrendingKeyword[]>([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [topSortBy, setTopSortBy] = useState<"opportunity_score" | "competition_level" | "keyword">("opportunity_score");
  const [topSortDir, setTopSortDir] = useState<"asc" | "desc">("desc");
  const [risingSortBy, setRisingSortBy] = useState<"trend_interest" | "competition_level" | "keyword">("trend_interest");
  const [risingSortDir, setRisingSortDir] = useState<"asc" | "desc">("desc");
  const [risingTimeRange, setRisingTimeRange] = useState<"today" | "this-week" | "this-month">("this-month");

  // Niche filter — persisted
  const [nicheFilter, setNicheFilter] = useState<string>(() => {
    try { return localStorage.getItem(NICHE_STORAGE_KEY) || "All"; } catch { return "All"; }
  });

  // Saved keywords
  const [savedKeywords, setSavedKeywords] = useState<SavedKeyword[]>([]);

  // Keyword groups
  const [keywordGroups, setKeywordGroups] = useState<KeywordGroup[]>([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [addToGroupKeyword, setAddToGroupKeyword] = useState<string | null>(null);

  // ── Loading text rotation ──────────────────────────────────
  const loadingTexts = [
    "Discovering viral opportunities...",
    "Analyzing search trends...",
    "Scoring competition levels...",
    "Finding long-tail goldmines...",
    "Mapping trend momentum...",
    "Calculating opportunity scores...",
  ];

  useEffect(() => {
    if (!isLoading) return;
    let i = 0;
    setLoadingText(loadingTexts[0]);
    const interval = setInterval(() => {
      i = (i + 1) % loadingTexts.length;
      setLoadingText(loadingTexts[i]);
    }, 2000);
    return () => clearInterval(interval);
  }, [isLoading]);

  // ── Data Loading ───────────────────────────────────────────
  useEffect(() => {
    loadHistory();
    loadAllTrending();
    loadSavedKeywords();
    loadKeywordGroups();
  }, []);

  // Persist niche filter
  useEffect(() => {
    try { localStorage.setItem(NICHE_STORAGE_KEY, nicheFilter); } catch {}
  }, [nicheFilter]);

  const loadHistory = async () => {
    const { data } = await supabase
      .from("keyword_searches")
      .select("id, keyword, video_title, created_at, results")
      .order("created_at", { ascending: false })
      .limit(30);
    if (data) setHistory(data as SearchHistoryItem[]);
  };

  const loadAllTrending = async () => {
    setTrendingLoading(true);
    const { data } = await supabase
      .from("trending_keywords")
      .select("*")
      .order("opportunity_score", { ascending: false })
      .limit(200);
    if (data) setAllTrending(data as unknown as TrendingKeyword[]);
    setTrendingLoading(false);
  };

  const loadSavedKeywords = async () => {
    const { data } = await supabase
      .from("saved_keywords")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setSavedKeywords(data as SavedKeyword[]);
  };

  const loadKeywordGroups = async () => {
    const { data } = await supabase
      .from("keyword_groups")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setKeywordGroups(data as unknown as KeywordGroup[]);
  };

  // ── Actions ────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!keyword.trim()) return;
    const credited = await consume("keyword-search", "Keyword research");
    if (!credited) return;
    setIsLoading(true);
    setResults(null);
    setActiveTab("overview");
    setResultsSubTab("overview");

    const MAX_RETRIES = 2;
    let lastError: any = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          setLoadingText("Retrying... hang tight");
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
        }

        const { data, error } = await supabase.functions.invoke("keyword-research", {
          body: { keyword: keyword.trim(), videoTitle: videoTitle.trim() || null },
        });

        // supabase.functions.invoke puts the response body in data even on error status
        if (error) {
          // Check if data contains a meaningful error message from our function
          if (data?.error === "rate_limit") { toast.error(data.message); setIsLoading(false); return; }
          if (data?.error) throw new Error(data.error);
          // Generic edge function error — retry
          throw error;
        }
        if (data?.error === "rate_limit") { toast.error(data.message); setIsLoading(false); return; }
        if (data?.error) throw new Error(data.error);

        setResults(data);
        loadHistory();
        setIsLoading(false);
        return;
      } catch (err: any) {
        lastError = err;
        console.error(`[keyword-research] attempt ${attempt + 1} failed:`, err);
        if (attempt < MAX_RETRIES) continue;
      }
    }

    const msg = lastError?.message || "Failed to analyze keyword";
    toast.error(msg.includes("non-2xx") ? "Search timed out — please try again." : msg);
    setIsLoading(false);
  };

  const toggleSaveKeyword = async (kw: string, nicheHint?: string, score?: number, comp?: string, trend?: number) => {
    const existing = savedKeywords.find(s => s.keyword === kw);
    if (existing) {
      await supabase.from("saved_keywords").delete().eq("id", existing.id);
      setSavedKeywords(prev => prev.filter(s => s.id !== existing.id));
      toast.success("Keyword removed");
    } else {
      if (!user) return;
      const { data, error } = await supabase.from("saved_keywords").insert({
        user_id: user.id,
        keyword: kw,
        niche: nicheHint || null,
        opportunity_score: score || 0,
        competition_level: comp || "medium",
        trend_interest: trend || 0,
        source: "search",
      }).select().single();
      if (!error && data) {
        setSavedKeywords(prev => [data as SavedKeyword, ...prev]);
        toast.success("Keyword saved ★");
      }
    }
  };

  const isKeywordSaved = (kw: string) => savedKeywords.some(s => s.keyword === kw);

  const createGroup = async () => {
    if (!newGroupName.trim() || !user) return;
    const { data, error } = await supabase.from("keyword_groups").insert({
      user_id: user.id,
      name: newGroupName.trim(),
      keywords: addToGroupKeyword ? [addToGroupKeyword] : [],
    }).select().single();
    if (!error && data) {
      setKeywordGroups(prev => [data as unknown as KeywordGroup, ...prev]);
      setNewGroupName("");
      setGroupDialogOpen(false);
      setAddToGroupKeyword(null);
      toast.success("Content Pillar created");
    }
  };

  const addKeywordToGroup = async (groupId: string, kw: string) => {
    const group = keywordGroups.find(g => g.id === groupId);
    if (!group) return;
    if (group.keywords.includes(kw)) { toast.info("Already in this pillar"); return; }
    const updated = [...group.keywords, kw];
    await supabase.from("keyword_groups").update({ keywords: updated }).eq("id", groupId);
    setKeywordGroups(prev => prev.map(g => g.id === groupId ? { ...g, keywords: updated } : g));
    setAddToGroupKeyword(null);
    toast.success(`Added to "${group.name}"`);
  };

  const removeKeywordFromGroup = async (groupId: string, kw: string) => {
    const group = keywordGroups.find(g => g.id === groupId);
    if (!group) return;
    const updated = group.keywords.filter(k => k !== kw);
    await supabase.from("keyword_groups").update({ keywords: updated }).eq("id", groupId);
    setKeywordGroups(prev => prev.map(g => g.id === groupId ? { ...g, keywords: updated } : g));
  };

  const deleteGroup = async (groupId: string) => {
    await supabase.from("keyword_groups").delete().eq("id", groupId);
    setKeywordGroups(prev => prev.filter(g => g.id !== groupId));
    toast.success("Content Pillar deleted");
  };

  // ── Sorting / Filtering ──────────────────────────────────
  const sortedKeywords = results?.relatedKeywords
    ? [...results.relatedKeywords].sort((a, b) => {
        if (sortBy === "competition") {
          const order = ["very_low", "low", "medium", "high", "very_high"];
          const diff = order.indexOf(a.competition) - order.indexOf(b.competition);
          return sortDir === "asc" ? diff : -diff;
        }
        if (sortBy === "relevance" && a.relevance !== undefined) {
          return sortDir === "desc" ? (b.relevance || 0) - (a.relevance || 0) : (a.relevance || 0) - (b.relevance || 0);
        }
        return sortDir === "desc" ? b.opportunityScore - a.opportunityScore : a.opportunityScore - b.opportunityScore;
      })
    : [];

  // Filter trending by niche
  const filteredTrending = useMemo(() => {
    if (nicheFilter === "All") return allTrending;
    return allTrending.filter(k => k.niche?.toLowerCase() === nicheFilter.toLowerCase());
  }, [allTrending, nicheFilter]);

  const sortedTopOpportunities = useMemo(() =>
    [...filteredTrending].sort((a, b) => {
      if (topSortBy === "keyword") return topSortDir === "asc" ? a.keyword.localeCompare(b.keyword) : b.keyword.localeCompare(a.keyword);
      if (topSortBy === "competition_level") {
        const order = ["very_low", "low", "medium", "high", "very_high"];
        const diff = order.indexOf(a.competition_level || "medium") - order.indexOf(b.competition_level || "medium");
        return topSortDir === "asc" ? diff : -diff;
      }
      return topSortDir === "desc" ? (b.opportunity_score || 0) - (a.opportunity_score || 0) : (a.opportunity_score || 0) - (b.opportunity_score || 0);
    }),
  [filteredTrending, topSortBy, topSortDir]);

  // Rising keywords with time filter
  const now = new Date();
  const sortedRising = useMemo(() => {
    const timeFiltered = filteredTrending.filter(k => {
      if ((k.trend_interest || 0) <= 0) return false;
      const lr = k.last_refreshed;
      if (!lr) return true;
      const rd = new Date(lr);
      if (risingTimeRange === "today") { const s = new Date(now.getFullYear(), now.getMonth(), now.getDate()); return rd >= s; }
      if (risingTimeRange === "this-week") { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); s.setHours(0, 0, 0, 0); return rd >= s; }
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return rd >= s;
    });
    return [...timeFiltered].sort((a, b) => {
      if (risingSortBy === "keyword") return risingSortDir === "asc" ? a.keyword.localeCompare(b.keyword) : b.keyword.localeCompare(a.keyword);
      if (risingSortBy === "competition_level") {
        const order = ["very_low", "low", "medium", "high", "very_high"];
        const diff = order.indexOf(a.competition_level || "medium") - order.indexOf(b.competition_level || "medium");
        return risingSortDir === "asc" ? diff : -diff;
      }
      return risingSortDir === "desc" ? (b.trend_interest || 0) - (a.trend_interest || 0) : (a.trend_interest || 0) - (b.trend_interest || 0);
    });
  }, [filteredTrending, risingTimeRange, risingSortBy, risingSortDir]);

  // Content gap opportunities — high score + low competition
  const contentGaps = useMemo(() =>
    filteredTrending.filter(k =>
      (k.opportunity_score || 0) >= 60 &&
      ["very_low", "low"].includes(k.competition_level || "medium")
    ).sort((a, b) => (b.opportunity_score || 0) - (a.opportunity_score || 0)).slice(0, 10),
  [filteredTrending]);

  // ── Export helpers ────────────────────────────────────────
  const exportCSV = () => {
    if (!sortedKeywords.length) return;
    const headers = ["Keyword", "Opportunity Score", "Trend Strength", "Competition", ...(videoTitle ? ["Relevance"] : [])];
    const rows = sortedKeywords.map(k => [k.keyword, k.opportunityScore, k.trendStrength, k.competition.replace("_", " "), ...(videoTitle && k.relevance !== undefined ? [k.relevance] : [])]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `keyword-research-${keyword.replace(/\s+/g, "-")}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  const exportTrendingCSV = (data: TrendingKeyword[], filename: string) => {
    if (!data.length) return;
    const headers = ["Keyword", "Niche", "Opportunity Score", "Trend Interest", "Competition"];
    const rows = data.map(k => [k.keyword, k.niche || "", k.opportunity_score || 0, k.trend_interest || 0, (k.competition_level || "medium").replace("_", " ")]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  const exportSavedCSV = () => {
    if (!savedKeywords.length) return;
    const headers = ["Keyword", "Niche", "Opportunity Score", "Competition", "Saved At"];
    const rows = savedKeywords.map(k => [k.keyword, k.niche || "", k.opportunity_score, k.competition_level, k.created_at]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "saved-keywords.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Saved keywords exported!");
  };

  const copyAll = () => {
    if (!sortedKeywords.length) return;
    navigator.clipboard.writeText(sortedKeywords.map(k => k.keyword).join("\n"));
    toast.success("All keywords copied!");
  };

  // ── Sort handlers ────────────────────────────────────────
  const handleSort = (col: typeof sortBy) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("desc"); } };
  const handleTopSort = (col: typeof topSortBy) => { if (topSortBy === col) setTopSortDir(d => d === "asc" ? "desc" : "asc"); else { setTopSortBy(col); setTopSortDir("desc"); } };
  const handleRisingSort = (col: typeof risingSortBy) => { if (risingSortBy === col) setRisingSortDir(d => d === "asc" ? "desc" : "asc"); else { setRisingSortBy(col); setRisingSortDir("desc"); } };

  // ── Keyword row action buttons ──────────────────────────
  const KeywordActions = ({ kw, niche, score, comp, trend }: { kw: string; niche?: string; score?: number; comp?: string; trend?: number }) => (
    <div className="flex items-center gap-0.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={(e) => { e.stopPropagation(); toggleSaveKeyword(kw, niche, score, comp, trend); }}
            className="p-1 rounded hover:bg-muted/50 transition-colors"
          >
            <Star className={`w-3.5 h-3.5 ${isKeywordSaved(kw) ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground"}`} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{isKeywordSaved(kw) ? "Remove from saved" : "Save keyword"}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/app/nexis-ai?keyword=${encodeURIComponent(kw)}`); }}
            className="p-1 rounded hover:bg-muted/50 transition-colors"
          >
            <Send className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">Create Script →</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/app/monetization-ai/new?keyword=${encodeURIComponent(kw)}`); }}
            className="p-1 rounded hover:bg-muted/50 transition-colors"
          >
            <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">Find Products →</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={(e) => { e.stopPropagation(); setAddToGroupKeyword(kw); }}
            className="p-1 rounded hover:bg-muted/50 transition-colors"
          >
            <FolderPlus className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">Add to Content Pillar</TooltipContent>
      </Tooltip>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
            <img src={keywordLogo} alt="Keywords" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Keyword Research</h1>
            <p className="text-sm text-muted-foreground">Discover high-opportunity topics for your content</p>
          </div>
        </div>
      </div>

      {/* Niche Filter Bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {NICHES.map(n => (
          <button
            key={n}
            onClick={() => setNicheFilter(n)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              nicheFilter === n
                ? "bg-primary text-primary-foreground"
                : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            {n}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="glass-card p-1 mb-4">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search keywords e.g. 'home workouts for beginners'"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="border-0 bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:ring-0 text-base h-12"
          />
          <Button onClick={handleSearch} disabled={isLoading || !keyword.trim()} size="icon" variant="ghost" className="h-10 w-10 shrink-0">
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowUpRight className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Optional video title */}
      <div className="glass-card p-1 mb-6">
        <Input
          placeholder="Paste your video title here for relevance check (optional)"
          value={videoTitle}
          onChange={(e) => setVideoTitle(e.target.value)}
          className="border-0 bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:ring-0 text-sm h-10"
        />
      </div>

      {/* Main Tabs */}
      <div className="flex items-center gap-1 mb-6 flex-wrap">
        {([
          { key: "overview" as PageTab, label: "Overview" },
          { key: "top-opportunities" as PageTab, label: "Top Opportunities" },
          { key: "rising-keywords" as PageTab, label: "Rising Keywords" },
          { key: "history" as PageTab, label: "History & Saved" },
        ]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && activeTab === "overview" && (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
            <Search className="w-6 h-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-muted-foreground animate-pulse text-sm">{loadingText}</p>
        </div>
      )}

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === "overview" && !isLoading && (
        <>
          {!results ? (
            /* ── Default state: show trending data ── */
            <div className="space-y-8">
              {/* Content Gap Opportunities */}
              {contentGaps.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Target className="w-5 h-5 text-amber-400" />
                    <h2 className="text-lg font-bold text-foreground">Low Competition Opportunities</h2>
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">Content Gap</Badge>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {contentGaps.map((kw, i) => (
                      <div key={i} className="glass-card p-4 border-amber-500/20 hover:border-amber-500/40 transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-sm text-foreground">{kw.keyword}</h3>
                          <span className={`text-xs font-bold ${scoreColor(kw.opportunity_score)}`}>{kw.opportunity_score}</span>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className={`text-[10px] ${competitionBadge(kw.competition_level)}`}>
                            {(kw.competition_level || "low").replace("_", " ")}
                          </Badge>
                          <TrendArrow interest={kw.trend_interest || 0} />
                          <MiniSparkline interest={kw.trend_interest || 0} />
                          <span className="text-[10px] text-muted-foreground ml-auto">{kw.niche}</span>
                        </div>
                        <KeywordActions kw={kw.keyword} niche={kw.niche} score={kw.opportunity_score} comp={kw.competition_level} trend={kw.trend_interest} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trending Now */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="w-5 h-5 text-orange-400" />
                  <h2 className="text-lg font-bold text-foreground">Trending Now</h2>
                  <span className="text-xs text-muted-foreground">Updated daily</span>
                </div>
                {trendingLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredTrending.length === 0 ? (
                  <div className="glass-card p-8 text-center">
                    <TrendingUp className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                    <p className="text-muted-foreground text-sm">No trending keywords found for this niche yet. Try selecting "All" or searching above.</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredTrending.slice(0, 15).map((kw, i) => (
                      <div
                        key={i}
                        className={`glass-card p-4 hover:border-green-500/40 transition-all cursor-pointer ${
                          contentGaps.some(c => c.keyword === kw.keyword) ? "border-amber-500/20" : "border-border/30"
                        }`}
                        onClick={() => { setKeyword(kw.keyword); handleSearch(); }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-sm text-foreground">{kw.keyword}</h3>
                          <span className={`inline-flex items-center justify-center w-8 h-6 rounded text-xs font-bold ${scoreBg(kw.opportunity_score)} ${scoreColor(kw.opportunity_score)}`}>
                            {kw.opportunity_score}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] ${competitionBadge(kw.competition_level)}`}>
                            {(kw.competition_level || "medium").replace("_", " ")}
                          </Badge>
                          <TrendArrow interest={kw.trend_interest || 0} />
                          <MiniSparkline interest={kw.trend_interest || 0} />
                          <span className="text-[10px] text-muted-foreground ml-auto">{kw.niche}</span>
                        </div>
                        {kw.long_tails && kw.long_tails.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {(kw.long_tails as string[]).slice(0, 3).map((lt, j) => (
                              <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">{lt}</span>
                            ))}
                          </div>
                        )}
                        <KeywordActions kw={kw.keyword} niche={kw.niche} score={kw.opportunity_score} comp={kw.competition_level} trend={kw.trend_interest} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rising This Week */}
              {sortedRising.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <h2 className="text-lg font-bold text-foreground">Rising This Week</h2>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {sortedRising.slice(0, 9).map((kw, i) => (
                      <div key={i} className="glass-card p-4 border-green-500/10 hover:border-green-500/30 transition-all cursor-pointer" onClick={() => { setKeyword(kw.keyword); }}>
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-sm text-foreground">{kw.keyword}</h3>
                          <span className="text-green-400 text-xs font-medium">↗ {kw.trend_interest}%</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className={`text-[10px] ${competitionBadge(kw.competition_level)}`}>
                            {(kw.competition_level || "medium").replace("_", " ")}
                          </Badge>
                          <MiniSparkline interest={kw.trend_interest || 0} />
                          <span className="text-[10px] text-muted-foreground ml-auto">{kw.niche}</span>
                        </div>
                        <KeywordActions kw={kw.keyword} niche={kw.niche} score={kw.opportunity_score} comp={kw.competition_level} trend={kw.trend_interest} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── Search Results ── */
            <>
              <div className="flex items-center gap-1 mb-6">
                {(["overview", "related"] as const).map((tab) => (
                  <button key={tab} onClick={() => setResultsSubTab(tab)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${resultsSubTab === tab ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {tab === "overview" ? "Overview" : "Related keywords"}
                  </button>
                ))}
                <button onClick={() => setResults(null)} className="ml-auto px-3 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                  ← Back to Trending
                </button>
              </div>

              {resultsSubTab === "overview" && (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Main Keyword Card */}
                  <div className="glass-card p-6">
                    <div className="text-sm text-muted-foreground mb-1">Keyword analysis</div>
                    <h2 className="text-xl font-bold text-foreground mb-6">Overview: {results.mainKeyword.keyword}</h2>

                    <div className="flex flex-col items-center mb-6">
                      <div className="relative w-48 h-28 mb-2">
                        <svg viewBox="0 0 200 110" className="w-full h-full">
                          <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="hsl(var(--muted))" strokeWidth="12" strokeLinecap="round" />
                          <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke={results.mainKeyword.opportunityScore >= 60 ? "hsl(142, 71%, 45%)" : results.mainKeyword.opportunityScore >= 30 ? "hsl(48, 96%, 53%)" : "hsl(0, 84%, 60%)"} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(results.mainKeyword.opportunityScore / 100) * 251.2} 251.2`} />
                        </svg>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
                          <span className={`text-4xl font-bold ${scoreColor(results.mainKeyword.opportunityScore)}`}>{results.mainKeyword.opportunityScore}</span>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">Overall score</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-center mb-4">
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Search volume</div>
                        <div className="text-2xl font-bold text-foreground">{results.mainKeyword.searchVolume}</div>
                        <Badge className={`mt-1 ${competitionBadge(results.mainKeyword.competition)}`}>{results.mainKeyword.competition.replace("_", " ")}</Badge>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Trend</div>
                        <div className="text-2xl">{results.mainKeyword.trendEmoji}</div>
                        <div className="text-sm font-medium text-foreground capitalize">{results.mainKeyword.trendStatus}</div>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{results.mainKeyword.explanation}</p>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/app/nexis-ai?keyword=${encodeURIComponent(results.mainKeyword.keyword)}`)}>
                        <Send className="w-3.5 h-3.5 mr-1" /> Create Script
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/app/monetization-ai/new?keyword=${encodeURIComponent(results.mainKeyword.keyword)}`)}>
                        <TrendingUp className="w-3.5 h-3.5 mr-1" /> Find Products
                      </Button>
                      <button onClick={() => toggleSaveKeyword(results.mainKeyword.keyword, undefined, results.mainKeyword.opportunityScore, results.mainKeyword.competition)} className="p-1.5 rounded hover:bg-muted/50">
                        <Star className={`w-4 h-4 ${isKeywordSaved(results.mainKeyword.keyword) ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground"}`} />
                      </button>
                    </div>
                  </div>

                  {/* Title Analysis or Related Preview */}
                  <div className="glass-card p-6">
                    {results.titleAnalysis ? (
                      <>
                        <div className="text-sm text-muted-foreground mb-1">How relevant this keyword is to your video title</div>
                        <h2 className="text-xl font-bold text-foreground mb-4">Title Relevance</h2>
                        <div className="flex items-center gap-4 mb-4">
                          <div className={`text-5xl font-bold ${scoreColor(results.titleAnalysis.relevanceScore)}`}>{results.titleAnalysis.relevanceScore}</div>
                          <div className="text-sm text-muted-foreground">/ 100 relevance score</div>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <p className="text-sm text-foreground leading-relaxed">{results.titleAnalysis.advice}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-muted-foreground mb-1">Keywords related to your search</div>
                        <h2 className="text-xl font-bold text-foreground mb-4">Related keywords</h2>
                        <div className="overflow-hidden rounded-lg border border-border">
                          <Table>
                            <TableHeader><TableRow className="border-border"><TableHead className="text-muted-foreground">Keyword</TableHead><TableHead className="text-muted-foreground text-right">Score</TableHead></TableRow></TableHeader>
                            <TableBody>
                              {sortedKeywords.slice(0, 5).map((kw, i) => (
                                <TableRow key={i} className="border-border">
                                  <TableCell className="text-foreground text-sm">{kw.keyword}</TableCell>
                                  <TableCell className="text-right">
                                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded text-xs font-bold ${scoreBg(kw.opportunityScore)} ${scoreColor(kw.opportunityScore)}`}>{kw.opportunityScore}</span>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        <button onClick={() => setResultsSubTab("related")} className="mt-4 mx-auto block px-4 py-2 rounded-full bg-muted/50 text-sm text-foreground hover:bg-muted transition-colors">
                          View all {sortedKeywords.length} related keywords
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {resultsSubTab === "related" && (
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h2 className="text-lg font-bold text-foreground">{sortedKeywords.length} Related & Long-tail Keywords</h2>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={exportCSV}><Download className="w-4 h-4 mr-1" /> Export</Button>
                      <Button variant="outline" size="sm" onClick={copyAll}><Copy className="w-4 h-4 mr-1" /> Copy all</Button>
                    </div>
                  </div>

                  <div className="overflow-auto rounded-lg border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className="text-muted-foreground cursor-pointer select-none" onClick={() => handleSort("opportunityScore")}>Keyword <SortIcon active={sortBy === "opportunityScore"} dir={sortDir} /></TableHead>
                          <TableHead className="text-muted-foreground text-center">Score</TableHead>
                          <TableHead className="text-muted-foreground text-center">Trend</TableHead>
                          <TableHead className="text-muted-foreground text-center cursor-pointer select-none" onClick={() => handleSort("competition")}>Competition <SortIcon active={sortBy === "competition"} dir={sortDir} /></TableHead>
                          {videoTitle && <TableHead className="text-muted-foreground text-center cursor-pointer select-none" onClick={() => handleSort("relevance")}>Relevance <SortIcon active={sortBy === "relevance"} dir={sortDir} /></TableHead>}
                          <TableHead className="text-muted-foreground text-center w-32">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedKeywords.map((kw, i) => (
                          <TableRow key={i} className="border-border hover:bg-muted/30">
                            <TableCell className="text-foreground text-sm font-medium">{kw.keyword}</TableCell>
                            <TableCell className="text-center">
                              <span className={`inline-flex items-center justify-center w-9 h-7 rounded text-xs font-bold ${scoreBg(kw.opportunityScore)} ${scoreColor(kw.opportunityScore)}`}>{kw.opportunityScore}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={`text-xs ${trendBadge(kw.trendStrength)}`}>{kw.trendStrength}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={`text-xs ${competitionBadge(kw.competition)}`}>{kw.competition.replace("_", " ")}</Badge>
                            </TableCell>
                            {videoTitle && kw.relevance !== undefined && <TableCell className="text-center"><span className={`text-sm font-medium ${scoreColor(kw.relevance)}`}>{kw.relevance}%</span></TableCell>}
                            <TableCell className="text-center">
                              <KeywordActions kw={kw.keyword} score={kw.opportunityScore} comp={kw.competition} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ==================== TOP OPPORTUNITIES TAB ==================== */}
      {activeTab === "top-opportunities" && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-lg font-bold text-foreground">Top keyword opportunities</h2>
            <Button variant="outline" size="sm" onClick={() => exportTrendingCSV(sortedTopOpportunities, "top-keyword-opportunities.csv")}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>

          {trendingLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : sortedTopOpportunities.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No trending keywords found. Try selecting "All" niches or check back tomorrow.</p>
            </div>
          ) : (
            <div className="overflow-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground cursor-pointer select-none" onClick={() => handleTopSort("keyword")}>Keyword <SortIcon active={topSortBy === "keyword"} dir={topSortDir} /></TableHead>
                    <TableHead className="text-muted-foreground text-center">Trend</TableHead>
                    <TableHead className="text-muted-foreground text-center cursor-pointer select-none" onClick={() => handleTopSort("competition_level")}>Competition <SortIcon active={topSortBy === "competition_level"} dir={topSortDir} /></TableHead>
                    <TableHead className="text-muted-foreground text-center cursor-pointer select-none" onClick={() => handleTopSort("opportunity_score")}>Score <SortIcon active={topSortBy === "opportunity_score"} dir={topSortDir} /></TableHead>
                    <TableHead className="text-muted-foreground text-center">Niche</TableHead>
                    <TableHead className="text-muted-foreground text-center w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTopOpportunities.map((kw, i) => {
                    const isGap = (kw.opportunity_score || 0) >= 60 && ["very_low", "low"].includes(kw.competition_level || "medium");
                    return (
                      <TableRow key={i} className={`border-border hover:bg-muted/30 ${isGap ? "bg-amber-500/5" : ""}`}>
                        <TableCell className="text-foreground text-sm font-medium">
                          {kw.keyword}
                          {isGap && <Badge className="ml-2 bg-amber-500/20 text-amber-400 border-amber-500/30 text-[9px]">Gap</Badge>}
                        </TableCell>
                        <TableCell className="text-center">
                          <TrendArrow interest={kw.trend_interest || 0} />
                          <MiniSparkline interest={kw.trend_interest || 0} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`text-xs ${competitionBadge(kw.competition_level || "medium")}`}>
                            {(kw.competition_level || "medium").replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center justify-center w-9 h-7 rounded text-xs font-bold ${scoreBg(kw.opportunity_score)} ${scoreColor(kw.opportunity_score)}`}>{kw.opportunity_score}</span>
                        </TableCell>
                        <TableCell className="text-center text-xs text-muted-foreground">{kw.niche}</TableCell>
                        <TableCell className="text-center">
                          <KeywordActions kw={kw.keyword} niche={kw.niche} score={kw.opportunity_score} comp={kw.competition_level} trend={kw.trend_interest} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* ==================== RISING KEYWORDS TAB ==================== */}
      {activeTab === "rising-keywords" && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-bold text-foreground mb-3">Rising keywords</h2>
              <div className="inline-flex items-center bg-muted/50 rounded-full p-1 gap-0.5">
                {([ { key: "today" as const, label: "Today" }, { key: "this-week" as const, label: "This week" }, { key: "this-month" as const, label: "This month" } ]).map((tab) => (
                  <button key={tab.key} onClick={() => setRisingTimeRange(tab.key)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${risingTimeRange === tab.key ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>{tab.label}</button>
                ))}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => exportTrendingCSV(sortedRising, "rising-keywords.csv")}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>

          {trendingLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : sortedRising.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Flame className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No rising keywords found for this time period. Try "This month" or select "All" niches.</p>
            </div>
          ) : (
            <div className="overflow-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground cursor-pointer select-none" onClick={() => handleRisingSort("keyword")}>Keyword <SortIcon active={risingSortBy === "keyword"} dir={risingSortDir} /></TableHead>
                    <TableHead className="text-muted-foreground text-center">Trend</TableHead>
                    <TableHead className="text-muted-foreground text-center cursor-pointer select-none" onClick={() => handleRisingSort("trend_interest")}>Growth <SortIcon active={risingSortBy === "trend_interest"} dir={risingSortDir} /></TableHead>
                    <TableHead className="text-muted-foreground text-center cursor-pointer select-none" onClick={() => handleRisingSort("competition_level")}>Competition <SortIcon active={risingSortBy === "competition_level"} dir={risingSortDir} /></TableHead>
                    <TableHead className="text-muted-foreground text-center w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRising.map((kw, i) => (
                    <TableRow key={i} className="border-border hover:bg-muted/30">
                      <TableCell className="text-foreground text-sm font-medium">{kw.keyword}</TableCell>
                      <TableCell className="text-center">
                        <TrendArrow interest={kw.trend_interest || 0} />
                        <MiniSparkline interest={kw.trend_interest || 0} />
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-green-400 text-sm font-medium">↗ {kw.trend_interest}%</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-xs ${competitionBadge(kw.competition_level || "medium")}`}>
                          {(kw.competition_level || "medium").replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <KeywordActions kw={kw.keyword} niche={kw.niche} score={kw.opportunity_score} comp={kw.competition_level} trend={kw.trend_interest} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* ==================== HISTORY & SAVED TAB ==================== */}
      {activeTab === "history" && (
        <div className="space-y-8">
          {/* Saved Keywords */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bookmark className="w-5 h-5 text-yellow-400" />
                <h2 className="text-lg font-bold text-foreground">Saved Keywords</h2>
                <span className="text-xs text-muted-foreground">({savedKeywords.length})</span>
              </div>
              {savedKeywords.length > 0 && (
                <Button variant="outline" size="sm" onClick={exportSavedCSV}>
                  <Download className="w-4 h-4 mr-1" /> Export CSV
                </Button>
              )}
            </div>

            {savedKeywords.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No saved keywords yet. Click the ★ icon on any keyword to save it for later.</p>
            ) : (
              <div className="overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">Keyword</TableHead>
                      <TableHead className="text-muted-foreground text-center">Score</TableHead>
                      <TableHead className="text-muted-foreground text-center">Competition</TableHead>
                      <TableHead className="text-muted-foreground text-center">Niche</TableHead>
                      <TableHead className="text-muted-foreground text-center w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {savedKeywords.map((kw) => (
                      <TableRow key={kw.id} className="border-border hover:bg-muted/30">
                        <TableCell className="text-foreground text-sm font-medium">{kw.keyword}</TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center justify-center w-9 h-7 rounded text-xs font-bold ${scoreBg(kw.opportunity_score)} ${scoreColor(kw.opportunity_score)}`}>{kw.opportunity_score}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`text-xs ${competitionBadge(kw.competition_level)}`}>{kw.competition_level.replace("_", " ")}</Badge>
                        </TableCell>
                        <TableCell className="text-center text-xs text-muted-foreground">{kw.niche || "—"}</TableCell>
                        <TableCell className="text-center">
                          <KeywordActions kw={kw.keyword} niche={kw.niche || undefined} score={kw.opportunity_score} comp={kw.competition_level} trend={kw.trend_interest} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Content Pillars / Keyword Groups */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FolderPlus className="w-5 h-5 text-purple-400" />
                <h2 className="text-lg font-bold text-foreground">Content Pillars</h2>
              </div>
              <Dialog open={groupDialogOpen && !addToGroupKeyword} onOpenChange={(open) => { setGroupDialogOpen(open); if (!open) setNewGroupName(""); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" onClick={() => setGroupDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" /> New Pillar
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Content Pillar</DialogTitle></DialogHeader>
                  <Input placeholder='e.g. "Beginner Fitness"' value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createGroup()} />
                  <DialogFooter>
                    <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                    <Button onClick={createGroup} disabled={!newGroupName.trim()}>Create</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {keywordGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No content pillars yet. Group related keywords into pillars for content planning.</p>
            ) : (
              <div className="space-y-4">
                {keywordGroups.map((group) => (
                  <Collapsible key={group.id}>
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-2">
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-sm text-foreground">{group.name}</span>
                          <span className="text-xs text-muted-foreground">({group.keywords.length} keywords)</span>
                        </div>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); deleteGroup(group.id); }}>
                          <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pl-6 pt-2 space-y-1">
                        {group.keywords.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2">No keywords yet. Use the folder icon on any keyword to add it here.</p>
                        ) : group.keywords.map((kw, i) => (
                          <div key={i} className="flex items-center justify-between py-1.5 px-3 rounded hover:bg-muted/30">
                            <span className="text-sm text-foreground">{kw}</span>
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setKeyword(kw); setActiveTab("overview"); }} className="text-xs text-primary hover:underline">Search</button>
                              <button onClick={() => removeKeywordFromGroup(group.id, kw)} className="p-1 text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </div>

          {/* Search History */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-lg font-bold text-foreground">Recent Searches</h2>
            </div>

            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No search history yet.</p>
            ) : (
              <div className="space-y-1">
                {history.map((h) => {
                  const resultData = h.results as any;
                  const score = resultData?.mainKeyword?.opportunityScore;
                  const comp = resultData?.mainKeyword?.competition;
                  return (
                    <button
                      key={h.id}
                      onClick={() => { setKeyword(h.keyword); if (h.video_title) setVideoTitle(h.video_title); setActiveTab("overview"); }}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted/50 text-sm transition-colors flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="font-medium text-foreground truncate">{h.keyword}</span>
                        {h.video_title && <span className="text-xs text-muted-foreground truncate">• {h.video_title}</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {score && <span className={`text-xs font-bold ${scoreColor(score)}`}>{score}</span>}
                        {comp && <Badge variant="outline" className={`text-[10px] ${competitionBadge(comp)}`}>{comp.replace("_", " ")}</Badge>}
                        <span className="text-[10px] text-muted-foreground">{new Date(h.created_at).toLocaleDateString()}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Add to Group Dialog ── */}
      <Dialog open={!!addToGroupKeyword} onOpenChange={(open) => { if (!open) setAddToGroupKeyword(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add "{addToGroupKeyword}" to a Content Pillar</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {keywordGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No pillars yet. Create one below.</p>
            ) : keywordGroups.map((group) => (
              <button
                key={group.id}
                onClick={() => addToGroupKeyword && addKeywordToGroup(group.id, addToGroupKeyword)}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted/50 text-sm font-medium text-foreground flex items-center justify-between"
              >
                {group.name}
                <span className="text-xs text-muted-foreground">{group.keywords.length} keywords</span>
              </button>
            ))}
          </div>
          <div className="flex gap-2 pt-2 border-t border-border">
            <Input placeholder="New pillar name..." value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createGroup()} className="text-sm" />
            <Button size="sm" onClick={createGroup} disabled={!newGroupName.trim()}>Create</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
